import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Currency } from '@/components/common/Currency';
import { getInvoiceStatusBadge } from '@/components/accounts-receivable/invoice-status-badge';
import { CustomerLink } from '@/components/accounts-receivable/customer-link';
import {
  FileTextIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CurrencyCircleDollarIcon,
  UserCircleIcon,
} from '@phosphor-icons/react';
import type { ArInvoiceDetail } from '@erp/rpc';

interface ARInvoiceMetadataProps {
  invoice: ArInvoiceDetail;
}

export function ARInvoiceMetadata({ invoice }: ARInvoiceMetadataProps) {
  const gt = useGT();
  const currency = invoice.currencyCode ?? 'USD';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileTextIcon className="h-4 w-4" />
            {invoice.invoiceNumber ??
              `(${gt('No invoice number', {
                context:
                  'AR invoice metadata card title fallback when no invoice number',
              })})`}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {invoice.isSent && (
              <Badge variant="outline" className="text-xs">
                <T>Sent</T>
              </Badge>
            )}
            {getInvoiceStatusBadge(invoice.status)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Prominent amounts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-muted/50 rounded-md p-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T>Amount</T>
            </div>
            <div className="text-sm font-semibold">
              <Currency
                value={invoice.netAmount}
                currency={currency}
                fallback="—"
              />
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T>Total Paid</T>
            </div>
            <div className="text-sm font-semibold">
              <Currency
                value={invoice.totalPaid}
                currency={currency}
                fallback="—"
              />
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T>Balance Due</T>
            </div>
            <div className="text-lg font-bold text-green-600">
              <Currency
                value={invoice.balanceDue}
                currency={currency}
                fallback="—"
              />
            </div>
          </div>
        </div>

        {/* Key fields grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T>Invoice Date</T>
            </div>
            <div className="font-medium text-sm">
              {invoice.invoiceDate
                ? formatDateOnlyString(invoice.invoiceDate)
                : '—'}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T>Due Date</T>
            </div>
            <div className="font-medium text-sm">
              {invoice.dueDate ? formatDateOnlyString(invoice.dueDate) : '—'}
            </div>
          </div>

          {invoice.salesOrder?.shipDate && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                <T>Ship Date</T>
              </div>
              <div className="font-medium text-sm">
                {formatDateOnlyString(invoice.salesOrder.shipDate)}
              </div>
            </div>
          )}

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              <T>Customer</T>
            </div>
            <CustomerLink
              customerId={invoice.customerId}
              customerName={invoice.customerName}
            />
            {invoice.customerCode && (
              <div className="text-xs text-muted-foreground">
                {`#${invoice.customerCode}`}
              </div>
            )}
          </div>

          {invoice.salesperson && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <UserCircleIcon className="h-3 w-3" />
                <T context="ar invoice metadata row label">Salesperson</T>
              </div>
              <div className="text-sm font-medium">
                {invoice.salesperson.name}
              </div>
              {invoice.salesperson.email && (
                <a
                  href={`mailto:${invoice.salesperson.email}`}
                  className="text-xs text-primary hover:underline truncate block"
                >
                  {invoice.salesperson.email}
                </a>
              )}
            </div>
          )}

          {invoice.paymentTermsDescription ||
          invoice.paymentTermDays != null ? (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CurrencyCircleDollarIcon className="h-3 w-3" />
                <T>Payment Terms</T>
              </div>
              <div className="text-sm">
                {invoice.paymentTermsDescription ??
                  gt('Net {days}', { days: String(invoice.paymentTermDays) })}
              </div>
            </div>
          ) : null}

          {invoice.customerPurchaseOrderReference && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <ShoppingCartIcon className="h-3 w-3" />
                <T>Customer PO</T>
              </div>
              <div className="font-medium text-sm">
                {invoice.customerPurchaseOrderReference}
              </div>
            </div>
          )}

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T>Currency</T>
            </div>
            <div className="text-sm">{invoice.currencyCode ?? '—'}</div>
          </div>
        </div>

        {/* Linked Sales Order */}
        {invoice.salesOrder && (
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ShoppingCartIcon className="h-3 w-3" />
              <T>Linked Sales Order</T>
            </div>
            <div className="text-sm">
              <Link
                to="/sales-orders/$salesOrderId"
                params={{ salesOrderId: invoice.salesOrder.id }}
                className="font-medium text-primary hover:underline"
              >
                {invoice.salesOrder.customerPurchaseOrderNumber ??
                  invoice.salesOrderId.slice(0, 8)}
              </Link>
              {invoice.salesOrder.orderDate && (
                <span className="text-muted-foreground ml-2">
                  {gt('Ordered: {date}', {
                    date: formatDateOnlyString(invoice.salesOrder.orderDate),
                  })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Source Info */}
        {invoice.originSystem && (
          <div className="border-t pt-3">
            <div className="text-xs text-muted-foreground">
              {invoice.originId
                ? gt('Source: {system} | Origin ID: {id}', {
                    system: invoice.originSystem,
                    id: invoice.originId,
                  })
                : gt('Source: {system}', { system: invoice.originSystem })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
